import type { Category, ConsentState, PublicConfig, WidgetOptions } from "./types";
import { detectLang, t } from "./i18n";
import { getOrCreateCookieId, loadState, saveState } from "./storage";
import { needsBanner, isGranted } from "./state";
import { fetchPublicConfig, recordCategory, withdrawRecords } from "./api";
import { activateGranted } from "./gating";
import { themeVars } from "./theme";
import { WIDGET_CSS } from "./ui/styles";
import { renderBanner } from "./ui/banner";
import { renderPreferences } from "./ui/preferences";
import { renderFab } from "./ui/fab";

const HOST_ID = "dp-consent-widget-host";

export async function initWidget(opts: WidgetOptions): Promise<HTMLElement> {
  if (document.getElementById(HOST_ID)) return document.getElementById(HOST_ID)!; // double-load guard

  let config: PublicConfig;
  try {
    config = await fetchPublicConfig(opts);
  } catch {
    return document.createElement("div"); // fail-safe: scripts stay blocked, no UI
  }
  if (!config.categories.some((c) => !c.alwaysOn)) return document.createElement("div"); // nothing to consent to

  const lang = opts.lang ?? detectLang();
  const strings = t(lang);
  const host = document.createElement("div");
  host.id = HOST_ID;
  document.body.appendChild(host);
  const shadow = host.attachShadow({ mode: "open" });
  const style = document.createElement("style");
  style.textContent = `:host { ${themeVars(config.branding)} } ${WIDGET_CSS}`;
  shadow.appendChild(style);
  const mount = document.createElement("div");
  shadow.appendChild(mount);

  const cookieId = getOrCreateCookieId();
  let state = loadState();

  const grantedKeys = (st: ConsentState | null) => config.categories.filter((c) => c.alwaysOn || isGranted(st, c.key)).map((c) => c.key);
  activateGranted(grantedKeys(state)); // apply prior consent immediately

  const persist = (categories: ConsentState["categories"]) => {
    state = { version: config.version, decidedAt: new Date().toISOString(), categories };
    saveState(state);
  };

  const clear = () => { mount.innerHTML = ""; };

  const showFab = () => { clear(); renderFab(mount, { strings, onClick: openPrefs }); };

  let inFlight = false;
  const applySelections = async (selections: Record<string, boolean>) => {
    if (inFlight) return;
    inFlight = true;
    try {
      const categories: ConsentState["categories"] = { ...(state?.categories ?? {}) };
      for (const cat of config.categories) {
        if (cat.alwaysOn) continue;
        const want = selections[cat.key];
        if (want === undefined) continue;
        if (want) {
          const recordIds = await recordCategory(opts, cookieId, cat.purposeIds);
          categories[cat.key] = { status: "given", recordIds };
          activateGranted([cat.key]);
        } else {
          const prior = state?.categories?.[cat.key]?.recordIds ?? [];
          if (prior.length) await withdrawRecords(opts, prior);
          categories[cat.key] = { status: "withdrawn" };
        }
      }
      persist(categories);
      showFab();
    } catch {
      // Network/API failure: keep the banner visible so the user can retry; do not persist a partial decision.
    } finally {
      inFlight = false;
    }
  };

  const openPrefs = () => {
    const granted: Record<string, boolean> = {};
    for (const c of config.categories) granted[c.key] = c.alwaysOn || isGranted(state, c.key);
    renderPreferences(mount, { lang, strings, categories: config.categories, granted, onSave: applySelections, onClose: showFab });
  };

  const acceptAll = () => applySelections(Object.fromEntries(config.categories.filter((c) => !c.alwaysOn).map((c) => [c.key, true])));
  const rejectAll = () => applySelections(Object.fromEntries(config.categories.filter((c) => !c.alwaysOn).map((c) => [c.key, false])));

  if (needsBanner(state, config.version, new Date())) {
    renderBanner(mount, { lang, strings, position: opts.position, policyUrl: config.client.policyUrl, onAccept: acceptAll, onReject: rejectAll, onCustomize: openPrefs });
  } else {
    showFab();
  }

  return host;
}

// Auto-init from the host <script data-dp-key="..."> tag.
function autoInit(): void {
  const tag = document.currentScript as HTMLScriptElement | null
    ?? document.querySelector<HTMLScriptElement>("script[data-dp-key]");
  if (!tag) return;
  const publishableKey = tag.getAttribute("data-dp-key");
  if (!publishableKey) return;
  const base = tag.getAttribute("data-dp-base") ?? new URL(tag.src).origin + "/api/v1/consent";
  const langAttr = tag.getAttribute("data-dp-lang") as WidgetOptions["lang"] | null;
  const posAttr = tag.getAttribute("data-dp-position") as WidgetOptions["position"] | null;
  initWidget({ publishableKey, baseUrl: base, lang: langAttr ?? undefined, position: posAttr ?? undefined }).catch(() => {});
}

if (typeof document !== "undefined") autoInit();
